import type {
  ItinerarySearchSortBy,
  ItinerarySearchSortOrder,
} from '@travel-planner/shared/schemas';

// Round-trip URL search params <-> typed filter object for /itineraries.
// Used by the route's `validateSearch` and consumed via `useSearch` in the
// itineraries table widget.
//
// Mirrors the products convention at
// `apps/products/web/src/features/products-filters/model/search-params.ts`:
// a pure URL coercer, no Zod parse, no exceptions. TanStack Router's
// `validateSearch` rebuilds this object on every URL parse so the function
// returns plain optional fields — fields the URL doesn't carry come back
// as `undefined`, which the tRPC transformer strips at the wire.

export type ItinerariesSearch = {
  title?: string;
  reference?: string;
  optigest?: string;
  customerName?: string;
  status?: string[];
  duration?: number[];
  // Agent-applied range filters (#331). The agent's price/duration comparisons
  // land here via the apply_filters envelope; no manual UI control yet.
  minDuration?: number;
  maxDuration?: number;
  minPrice?: number;
  maxPrice?: number;
  owners?: string[];
  customers?: string[];
  labels?: string[];
  startDate?: string;
  endDate?: string;
  excludeStartDate?: string;
  excludeEndDate?: string;
  // Range on the itinerary's created-on date (distinct from the travel-date
  // range above). Both bounds ship to the backend only when both are set.
  createdStartDate?: string;
  createdEndDate?: string;
  travelDate?: string;
  myItineraries?: boolean;
  // XT-723 — narrow to itineraries carrying unanswered Customer Preview
  // comments. Boolean toggle; only present in the URL when on.
  pendingComments?: boolean;
  viewed?: number;
  sortBy?: ItinerarySearchSortBy;
  sortOrder?: ItinerarySearchSortOrder;
  page?: number;
  // The filter DSL string the agent applied (epic #273). Display-only — the
  // structured fields above drive the actual tRPC search; this is rendered
  // verbatim in the toolbar filter chip so the consultant sees what the
  // agent "thought" the filter was.
  dslFilter?: string;
};

const asString = (v: unknown): string | undefined =>
  typeof v === 'string' && v.length > 0 ? v : undefined;

const asStringArray = (v: unknown): string[] | undefined => {
  if (!Array.isArray(v)) return undefined;
  const out = v.filter((x): x is string => typeof x === 'string');
  return out.length > 0 ? out : undefined;
};

const asNumber = (v: unknown): number | undefined => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.length > 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
};

const asNumberArray = (v: unknown): number[] | undefined => {
  if (!Array.isArray(v)) return undefined;
  const out: number[] = [];
  for (const x of v) {
    const n = asNumber(x);
    if (n !== undefined) out.push(n);
  }
  return out.length > 0 ? out : undefined;
};

const asBoolean = (v: unknown): boolean | undefined => {
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return undefined;
};

const SORT_BY_VALUES: ReadonlyArray<ItinerarySearchSortBy> = [
  'travelDate',
  'reference',
  'title',
  'modifiedOn',
  'createdOn',
  'price',
  'duration',
  'viewedTimes',
  'customer',
  'travelers',
  'status',
];

const asSortBy = (v: unknown): ItinerarySearchSortBy | undefined =>
  typeof v === 'string' && (SORT_BY_VALUES as ReadonlyArray<string>).includes(v)
    ? (v as ItinerarySearchSortBy)
    : undefined;

const asSortOrder = (v: unknown): ItinerarySearchSortOrder | undefined =>
  v === 'asc' || v === 'desc' ? v : undefined;

export const validateItinerariesSearch = (
  input: Record<string, unknown>
): ItinerariesSearch => ({
  title: asString(input.title),
  reference: asString(input.reference),
  optigest: asString(input.optigest),
  customerName: asString(input.customerName),
  status: asStringArray(input.status),
  duration: asNumberArray(input.duration),
  minDuration: asNumber(input.minDuration),
  maxDuration: asNumber(input.maxDuration),
  minPrice: asNumber(input.minPrice),
  maxPrice: asNumber(input.maxPrice),
  owners: asStringArray(input.owners),
  customers: asStringArray(input.customers),
  labels: asStringArray(input.labels),
  startDate: asString(input.startDate),
  endDate: asString(input.endDate),
  excludeStartDate: asString(input.excludeStartDate),
  excludeEndDate: asString(input.excludeEndDate),
  createdStartDate: asString(input.createdStartDate),
  createdEndDate: asString(input.createdEndDate),
  travelDate: asString(input.travelDate),
  myItineraries: asBoolean(input.myItineraries),
  pendingComments: asBoolean(input.pendingComments),
  viewed: asNumber(input.viewed),
  sortBy: asSortBy(input.sortBy),
  sortOrder: asSortOrder(input.sortOrder),
  page: asNumber(input.page),
  dslFilter: asString(input.dslFilter),
});
